<template>
  <div>
    <!-- 검색 및 필터 -->
    <div class="filter-container">
      <input v-model="nameFilter" placeholder="이름 검색" />
      <select v-model="bodyTypeFilter">
        <option value="">전체 체질</option>
        <option value="태양인">태양인</option>
        <option value="태음인">태음인</option>
        <option value="소양인">소양인</option>
        <option value="소음인">소음인</option>
      </select>
      <select v-model="sortKey">
        <option value="name">이름순</option>
        <option value="createdAt">가입일순</option>
        <option value="lydia">리디아순</option>
      </select>
      <select v-model="sortOrder">
        <option value="asc">오름차순</option>
        <option value="desc">내림차순</option>
      </select>
    </div>

    <table>
      <thead>
        <tr>
          <th>#</th>
          <th>이름</th>
          <th>성별</th>
          <th>생년월일</th>
          <th>체질</th>
          <th>리디아</th>
          <th>가입일</th>
        </tr>
      </thead>
      <tbody v-if="loaded">
        <tr
          v-for="(user, i) in paginatedUsers"
          :key="user.accessToken"
          @click="selectUser(user)"
          style="cursor: pointer"
        >
          <td>{{ totalCount - ((currentPage - 1) * pageSize + i) }}</td>
          <td>{{ user.name }}</td>
          <td>{{ user.gender }}</td>
          <td>{{ formatBirth(user.birthDate, user.calendarType) }}</td>
          <td>{{ user.bodyType }}</td>
          <td>{{ user.lydia ?? 0 }}</td>
          <td>{{ formatDate(user.createdAt) }}</td>
        </tr>
      </tbody>
      <tbody v-else>
        <tr>
          <td colspan="7" style="text-align: center">
            사용자 데이터를 불러오는 중입니다...
          </td>
        </tr>
      </tbody>
    </table>

    <div class="pagination">
      <button @click="loadPrevPage" :disabled="currentPage === 1">이전</button>
      <span>{{ currentPage }}</span>
      <button @click="loadNextPage" :disabled="isEnd">다음</button>
    </div>

    <div v-if="selectedUser">
      <h3>리디아 이력 - {{ selectedUser.name }}</h3>

      <div style="margin-bottom: 1rem">
        <button
          @click="adjustLydia(100)"
          class="primary-button"
          :disabled="loading"
        >
          +100 충전
        </button>
        <button
          @click="adjustLydia(-100)"
          class="back-button"
          :disabled="loading"
        >
          -100 차감
        </button>
      </div>

      <ul>
        <li v-for="(log, i) in lydiaHistory" :key="i">
          {{ formatDate(log.createdAt) }} |
          {{ log.type === 'charge' || log.type === 'reward' ? '+' : '-'
          }}{{ log.amount }} → {{ log.lydiaAfter }} | {{ log.menuUsed }}
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup>
  import { ref, onMounted, computed } from 'vue'
  import { collection, getDocs, query, orderBy } from 'firebase/firestore'
  import { db } from '@/firebase'
  import { GraphQLClient, gql } from 'graphql-request'

  const graphqlClient = new GraphQLClient(import.meta.env.VITE_API_ENDPOINT)

  const users = ref([])
  const selectedUser = ref(null)
  const lydiaHistory = ref([])
  const loading = ref(false)
  const loaded = ref(false)

  const pageSize = 10
  const currentPage = ref(1)
  const totalCount = ref(0)
  const isEnd = ref(false)

  const nameFilter = ref('')
  const bodyTypeFilter = ref('')
  const sortKey = ref('createdAt')
  const sortOrder = ref('desc')

  const formatBirth = (birthDate, calendarType) => {
    if (!birthDate) return ''
    const formatted = `${birthDate.slice(0, 4)}-${birthDate.slice(4, 6)}-${birthDate.slice(6)}`
    return `${formatted} (${calendarType === 'lunar' ? '음' : '양'})`
  }

  const formatDate = (iso) => {
    const d = new Date(iso)
    const pad = (n) => n.toString().padStart(2, '0')
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
  }

  const filteredSortedUsers = computed(() => {
    return [...users.value]
      .filter(
        (u) =>
          (!nameFilter.value || u.name?.includes(nameFilter.value)) &&
          (!bodyTypeFilter.value || u.bodyType === bodyTypeFilter.value),
      )
      .sort((a, b) => {
        let result = 0
        if (sortKey.value === 'name') {
          result = (a.name ?? '').localeCompare(b.name ?? '')
        } else if (sortKey.value === 'lydia') {
          result = (a.lydia ?? 0) - (b.lydia ?? 0)
        } else if (sortKey.value === 'createdAt') {
          result = new Date(a.createdAt ?? 0) - new Date(b.createdAt ?? 0)
        }
        return sortOrder.value === 'asc' ? result : -result
      })
  })

  const paginatedUsers = computed(() => {
    const start = (currentPage.value - 1) * pageSize
    return filteredSortedUsers.value.slice(start, start + pageSize)
  })

  const loadPrevPage = () => {
    if (currentPage.value > 1) currentPage.value--
  }

  const loadNextPage = () => {
    const start = currentPage.value * pageSize
    if (start < filteredSortedUsers.value.length) {
      currentPage.value++
    } else {
      isEnd.value = true
    }
  }

  const fetchUsers = async () => {
    loaded.value = false
    const q = query(collection(db, 'users'), orderBy('createdAt', 'desc'))
    const snapshot = await getDocs(q)
    users.value = snapshot.docs.map((doc) => ({
      accessToken: doc.id,
      ...doc.data(),
    }))
    totalCount.value = users.value.length
    loaded.value = true
  }

  onMounted(fetchUsers)

  const selectUser = async (user) => {
    selectedUser.value = user
    lydiaHistory.value = []
    const ref = collection(db, 'users', user.accessToken, 'lydia_history')
    const snapshot = await getDocs(ref)
    lydiaHistory.value = snapshot.docs
      .map((doc) => doc.data())
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
  }

  const adjustLydia = async (amount) => {
    if (!selectedUser.value) return
    const accessToken = selectedUser.value.accessToken
    const mutation = gql`
      mutation AdminAdjustLydia($accessToken: String!, $amount: Int!) {
        adminAdjustLydia(accessToken: $accessToken, amount: $amount) {
          result
          message
          newLydiaBalance
        }
      }
    `
    try {
      loading.value = true
      const data = await graphqlClient.request(mutation, {
        accessToken,
        amount,
      })
      const result = data?.adminAdjustLydia
      if (result?.result) {
        selectedUser.value.lydia = result.newLydiaBalance
        alert(result.message)
        await selectUser(selectedUser.value)
      } else {
        alert(result?.message || '리디아 조정 실패')
      }
    } catch (err) {
      alert('리디아 조정 실패: ' + err.message)
    } finally {
      loading.value = false
    }
  }
</script>

<style scoped>
  .filter-container {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-bottom: 1rem;
  }
  .filter-container input {
    flex: 1;
  }
  .filter-container select {
    padding: 0.5rem 0;
    font-size: 0.85rem;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.8rem;
    margin-top: 1rem;
  }
  th,
  td {
    padding: 0.4rem;
    border: 1px solid #ccc;
    text-align: center;
  }
  ul {
    margin-top: 1rem;
    padding-left: 1rem;
  }
  li {
    font-size: 0.8rem;
    margin-bottom: 0.5rem;
  }
  .primary-button {
    padding: 0.7rem 1.5rem;
    font-size: 1rem;
    border: none;
    border-radius: 8px;
    background-color: #4caf50;
    color: white;
    cursor: pointer;
    margin: 0 5px;
  }
  .primary-button:disabled {
    background-color: #a5d6a7;
    cursor: not-allowed;
  }
  .primary-button:hover {
    background-color: #45a049;
  }
  .back-button {
    background-color: #f44336;
    color: white;
  }
  .back-button:disabled {
    background-color: #ef9a9a;
    cursor: not-allowed;
  }
  .back-button:hover {
    background-color: #da190b;
  }
  .pagination {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 1rem;
    margin: 1rem 0;
  }
</style>
